export interface PaginatedPackagerResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Packager[];
}

export interface PaginatedPackagesResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Package[];
}

export type NamingElementType =
  | 'STRING_LITERAL'
  | 'METADATA'
  | 'DATE_FORMAT'
  | 'DOCUMENT_DATA'
  | 'SPLIT_VALUE';
export type NamingSeparator = '-' | '_' | '.';

export interface NamingElement {
  type: NamingElementType;
  value: string;
  format?: string;
}

export interface NamingConfig {
  elements: NamingElement[];
  separator: NamingSeparator;
  max_length?: number;
}

export enum DocumentDataStatusFilters {
  PENDING_CREATION = 'PENDING_CREATION',
  PENDING_REVIEW = 'PENDING_REVIEW',
  REVIEWED = 'REVIEWED',
  MISCURATED = 'MISCURATED',
  ESCALATED = 'ESCALATED',
  VERIFIED = 'VERIFIED',
  TRANSMISSION_SUCCESSFUL = 'TRANSMISSION_SUCCESSFUL',
  TRANSMISSION_FAILED = 'TRANSMISSION_FAILED',
  EXCLUDED = 'EXCLUDED',
  PENDING_SPLIT = 'PENDING_SPLIT',
}

// System-only statuses are returned by the API and rendered as badges, but
// users do not pick them directly — drop them from any status dropdown.
export const USER_SELECTABLE_DOCUMENT_DATA_STATUSES: DocumentDataStatusFilters[] = [
  DocumentDataStatusFilters.PENDING_REVIEW,
  DocumentDataStatusFilters.REVIEWED,
  DocumentDataStatusFilters.MISCURATED,
  DocumentDataStatusFilters.ESCALATED,
  DocumentDataStatusFilters.VERIFIED,
  DocumentDataStatusFilters.EXCLUDED,
  DocumentDataStatusFilters.PENDING_SPLIT,
];

export enum PackagerStatuses {
  CREATED = 'CREATED',
  PROCESSING = 'PROCESSING',
  PROCESSING_SUCCESSFUL = 'PROCESSING_SUCCESSFUL',
  PROCESSING_FAILED = 'PROCESSING_FAILED',
  TRANSMISSION_SUCCESSFUL = 'TRANSMISSION_SUCCESSFUL',
  TRANSMISSION_FAILED = 'TRANSMISSION_FAILED',
}

export enum PackageStatuses {
  DRAFT = 'DRAFT',
  CREATED = 'CREATED',
  PROCESSING = 'PROCESSING',
  PROCESSING_SUCCESSFUL = 'PROCESSING_SUCCESSFUL',
  PROCESSING_FAILED = 'PROCESSING_FAILED',
  VERIFIED = 'VERIFIED',
  FROZEN = 'FROZEN',
  ORPHANED = 'ORPHANED',
  TRANSMISSION_SUCCESSFUL = 'TRANSMISSION_SUCCESSFUL',
  TRANSMISSION_FAILED = 'TRANSMISSION_FAILED',
}

export enum PackageEntryStatus {
  INCLUDED = 'INCLUDED',
  EXCLUDED = 'EXCLUDED',
  TRANSMISSION_SUCCESSFUL = 'TRANSMISSION_SUCCESSFUL',
  TRANSMISSION_FAILED = 'TRANSMISSION_FAILED',
}

export enum OutputFormatTypes {
  JSON = 'JSON',
  CSV = 'CSV',
}

export enum PackagerScheduleTypes {
  MANUAL = 'MANUAL',
  CRON_SCHEDULE = 'CRON_SCHEDULE',
  STREAM = 'STREAM',
}

export enum DeliveryScheduleTypes {
  MANUAL = 'MANUAL',
  CRON_SCHEDULE = 'CRON_SCHEDULE',
  AUTOMATIC = 'AUTOMATIC',
}

export enum DeliveryDataGroupingStrategiesTypes {
  ALL_IN_ONE = 'ALL_IN_ONE',
  SPLIT_BY_DOC = 'SPLIT_BY_DOC',
  METADATA_MATCH_AND_MERGE = 'METADATA_MATCH_AND_MERGE',
}

export enum DeliveryMode {
  INDIVIDUAL = 'individual',
  ZIP_ONLY = 'zip_only',
  BOTH = 'both',
}

export enum TemplateReportType {
  ACTIVITY = 'activity',
  MAT = 'mat',
  CT = 'ct',
  MAT_CT = 'mat_ct',
  COC = 'coc',
  VALIDATION = 'validation',
}

export enum TemplateFileFormat {
  CSV = 'CSV',
  JSON = 'JSON',
}

export enum FilterCondition {
  EQUALS = 'equals',
  IS_ONE_OF = 'is_one_of',
  CONTAINS = 'contains',
  NOT_CONTAINS = 'not_contains',
  BETWEEN = 'between',
  GREATER_THAN = 'greater_than',
  LESS_THAN = 'less_than',
}

export interface TemplateColumn {
  id: string;
  field: string;
  header: string;
  order: number;
}

export interface TemplateFilter {
  field: string;
  condition: FilterCondition;
  value: string | number | string[] | [string, string] | [number, number];
}

export interface TemplateNamingConfig {
  elements: NamingElement[];
  separator?: NamingSeparator;
  max_length?: number;
}

export interface OutputTemplate {
  report_type?: TemplateReportType;
  columns: TemplateColumn[];
  filters?: TemplateFilter[];
  split_by?: string | null;
  default_split_value?: string;
  file_format?: TemplateFileFormat;
  naming_config?: TemplateNamingConfig | null;
}

export interface CustomOutputConfig {
  templates: Record<string, OutputTemplate>;
}

export enum S3RegionTypes {
  US_EAST_1 = 'us-east-1',
  US_WEST_1 = 'us-west-1',
  US_WEST_2 = 'us-west-2',
  EU_WEST_1 = 'eu-west-1',
  EU_CENTRAL_1 = 'eu-central-1',
  AP_SOUTHEAST_1 = 'ap-southeast-1',
  AP_NORTHEAST_1 = 'ap-northeast-1',
  SA_EAST_1 = 'sa-east-1',
}

export enum DocumentDataExclusionReasons {
  FILE_UNUSABLE = 'file unusable',
  IMAGE_UNUSABLE = 'image unusable',
  DUPLICATE = 'duplicate',
  NO_ACTIVITY = 'no activity',
  MISSING_PAGES = 'missing pages',
  OUT_OF_SCOPE = 'out of scope',
  CONCATENATED = 'concatenated',
  TRANSLATED = 'translated',
  ROTATED = 'rotated',
  CANCELLED = 'cancelled',
  RE_INTRODUCED = 're-introduced',
}

export enum ParentRelationship {
  TRANSLATED = 'translated',
  DUPLICATE = 'duplicate',
  SPLIT = 'split',
  REINTRODUCED = 'reintroduced',
}

export enum DocumentDataStatuses {
  INCLUDED = 'INCLUDED',
  EXCLUDED = 'EXCLUDED',
}

export interface Packager {
  url?: string;
  id?: string;
  document_data_status_filter?: DocumentDataStatusFilters[];
  start_date_filter?: string | null;
  end_date_filter?: string | null;
  start_date?: string;
  end_date?: string;
  include_duplicates_in_delivery?: boolean;
  packages?: string[]; // returns an array of strings referencing a url for each package
  include_source_files_in_delivery?: boolean;
  include_parent_files_in_delivery?: boolean;
  parent_source_file_rel_types?: ParentRelationship[] | null;
  coc_report_start_date?: string | null;
  coc_report_end_date?: string | null;
  packaging_begin_date?: string | null;
  packaging_end_date?: string | null;
  delivery_cron_expression?: string;
  created_at?: string;
  updated_at?: string;
  uuid?: string;
  label?: string;
  packager_status?: PackagerStatuses;
  packager_schedule_type?: PackagerScheduleTypes;
  schedule_cron_expression?: string;
  last_run?: string;
  last_package_path?: string | null;
  document_id_filter?: string[];
  eb_id_filter?: string[];
  delivery_schedule_type?: DeliveryScheduleTypes;
  delivery_data_grouping_strategy?: DeliveryDataGroupingStrategiesTypes;
  delivery_data_grouping_metadata_key?: string;
  s3_bucket?: string;
  output_directory?: string;
  delivery_mode?: DeliveryMode;
  source_file_naming_config?: NamingConfig;
  custom_output_config?: CustomOutputConfig | null;
}

// API emits "COMPLETE" | "INCOMPLETE"; the enum also has "NOT_COMPUTED" but that
// is surfaced as a null `completeness` block, not this value.
export type CompletenessHealth = 'COMPLETE' | 'INCOMPLETE';

// A drill-down is a dict of raw query params (forward straight to the entries
// query), or null when there's nothing to link to.
export type Drilldown = Record<string, string> | null;

export interface CompletenessGapCause {
  // Always one of the fixed three: "no_extraction" | "empty_result" | "render_error".
  cause: string;
  label: string; // display form, e.g. "No extracted data"
  count: number;
  drilldown: Drilldown;
}

export interface PackageCompleteness {
  health: CompletenessHealth;
  is_complete: boolean;
  documents_to_deliver: number; // delivery candidates (non-excluded)
  documents_delivered: number; // of those, how many landed in the file
  gap: CompletenessGapCause[]; // the fixed 3 loss causes, always present even at count 0
  computed_at: string;
}

export interface DismissedReason {
  reason: string; // stored value (used for keys)
  label: string; // display form
  count: number;
}

export interface PackageValidation {
  unresolved_count: number;
  dismissed: DismissedReason[];
}

// One row per validation issue from the validation-report endpoint (POST, fetched
// on demand — NOT part of the count-only `validation` block on the package detail).
// Already scoped to this package's own data. Note: includes EXCLUDED entries, so to
// match the `unresolved_count` headline filter to delivery_status !== 'EXCLUDED' and
// dismissal_reason == null.
export interface ValidationReportRow {
  validation_id: string;
  field_label: string;
  issue: string;
  issue_detail: string | null;
  dismissal_reason: string | null;
  document_id: string;
  document_data_id: string;
  source_file_name: string;
  delivery_status: string;
}

export interface Package {
  url: string;
  id: string;
  extraction_batch_data?: string[];
  document_data?: {
    count: number;
    url: string;
  };
  package_entries?: string[];
  created_at: string;
  updated_at: string;
  uuid: string;
  status?: PackageStatuses;
  delivered_at?: string | null;
  // Set on the first successful delivery and never cleared: a package that has
  // delivered is not re-rendered or edited in place.
  transmitted_at?: string | null;
  available_transitions?: PackageStatuses[];
  completeness?: PackageCompleteness | null; // null/absent = not recorded (legacy / pre-feature API)
  rendered_at?: string | null;
  // Last operator entry add/edit/delete, and last change to a document the
  // package renders. is_stale compares both against rendered_at.
  entries_changed_at?: string | null;
  data_changed_at?: string | null;
  // null = unknowable (never rendered, or rendered before rendered_at existed).
  // Deliberately tri-state: unknown must not read as fresh.
  is_stale?: boolean | null;
  validation?: PackageValidation; // detail GET only; absent on list/writes
}

export interface DocumentDataEntry {
  url: string;
  id: string;
  transformed_data_urls: Record<string, string>;
  created_at: string;
  updated_at: string;
  uuid: string;
  status: DocumentDataStatuses;
  document_data_status?: DocumentDataStatusFilters;
  exclusion_reason: DocumentDataExclusionReasons | null;
  escalation_detail?: string;
  is_md5_check_completed: boolean;
  is_label_check_completed: boolean;
  is_content_check_completed: boolean;
  source_file_id: string;
  source_file_name: string;
  source_file_md5: string | null;
  source_file_num_pages: number;
  source_file_size: number | null;
  received_at: string;
  source_file_detected_language: string | null;
  canonical_document_data: Record<string, unknown> | null;
  document: string;
  available_transitions?: DocumentDataStatusFilters[];
}

export interface PackageEntry {
  url: string;
  id: string;
  _persistent_id_map: Record<string, any>;
  created_at: string;
  updated_at: string;
  uuid: string;
  status_in_package: PackageEntryStatus;
  source_file_name: string;
  source_file_id: string;
  filename_in_package: string | null;
  exclusion_reason: string | null;
  delivered_at: string | null;
  package: string;
  document_data: string;
  // Live status of the source DocumentData. Entry status is derived from it once
  // at package creation and never re-derived, so the two can disagree.
  document_data_status?: DocumentDataStatusFilters | null;
  available_transitions?: PackageEntryStatus[];
}

export type DocumentRelationshipDetectionMethod = 'MD5' | 'CONTENT' | 'n/a';

export interface DocumentRelationship {
  url?: string;
  id: string;
  uuid?: string;
  created_at?: string;
  updated_at?: string;
  source_document_data: string;
  target_document_data: string;
  relationship_type_label: ParentRelationship | string;
  detection_method: DocumentRelationshipDetectionMethod;
}

export interface PaginatedPackageEntriesResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: PackageEntry[];
}

// Type for editing packages - makes all properties optional
export type IPartialPackage = Partial<Package>;

export interface PackagerAttributes {
  attributes: {
    data_pool: string[];
    extraction_batch: string[];
    document: string[];
    organization: string[];
    document_data: string[];
  };
  // Curated naming-eligible DOCUMENT_DATA fields (schema DOCUMENT_DATA_NAMING_FIELDS).
  document_data_fields: string[];
}

export interface ChainOfCustodyReportEntry {
  ChainOfCustodyStartDate: string;
  ChainOfCustodyEndDate: string;
  SourceFileReceivedAt: string;
  SourceFileID: string;
  SourceFileOriginalName: string;
  SourceFileOriginalPath: string;
  SourceFileFinalName: string;
  SourceFileStatus: string;
  ExclusionReason: string | null;
  ParentFileID: string | null;
  RelationshipToParent: string | null;
  PackageID: string;
  DeliveredAt: string;
  SourceFileNumPages: number;
  SourceFileDetectedLanguage: string | null;
  SourceFileSize: number;
}

export type ChainOfCustodyReportData = ChainOfCustodyReportEntry[];

export interface ChainOfCustodyReportResponse {
  success: boolean;
  data: ChainOfCustodyReportEntry[];
  format: OutputFormatTypes | string;
  packager_id: number;
  generated_at: string;
  size: number;
}

// Interim async response while CoC report is being generated
export interface ChainOfCustodyGeneratingResponse {
  status: 'generating' | 'success' | 'failed';
  message: string;
  retry_after?: number;
  packager_id: string;
}

export interface PackagePreviewSourceFile {
  filename?: string;
  content_type?: string;
}

export interface PackagePreviewEntry {
  filename?: string;
  // Content is JSON serialized as a string
  content?: string | Record<string, unknown> | null;
  source_files?: PackagePreviewSourceFile[];
  source_file_names?: string[];
}

// 202 from re-render / create-from-package: the work is queued, poll the package.
export interface PackageTaskResponse {
  task_id: string;
  package_id?: string;
  source_package_id?: string;
  status: 'PROCESSING';
}

export interface PackagePreviewResponse {
  data: PackagePreviewEntry[];
  errors?: any; // fixme?
  output_format?: OutputFormatTypes;
  success: boolean;
  delivery_data_grouping_strategy: DeliveryDataGroupingStrategiesTypes;
}
